import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Icon, Button } from '../../../pages/DesignSystem';
import type { EventAction } from '../../../types/organizer-events.types';

interface EventCardMenuProps {
  eventId: string;
  eventStatus: 'active' | 'draft' | 'cancelled';
  seriesId?: string;
  activeTab?: 'active' | 'draft' | 'past';
  onEdit: () => void;
  onDuplicate: () => void;
  onUnpublish?: () => void;
  onPublish?: () => void;
  onDelete?: () => void;
  onAddOccurrence?: () => void;
  onCancel?: () => void;
  onRestore?: () => void;
  onInstagram?: () => void;
  isSeriesMenu?: boolean;
}

interface MenuItem {
  id: EventAction;
  label: string;
  icon: string;
  isDanger?: boolean;
  show: boolean;
}

/**
 * EventCardMenu - Kebab menu (3 dots) with actions for event cards
 *
 * Actions:
 * - Edit: Edit the event
 * - Duplicate: Duplicate the event
 * - Unpublish: Unpublish the event (only for active events)
 */
const EventCardMenu: React.FC<EventCardMenuProps> = ({
  eventStatus,
  activeTab,
  onEdit,
  onDuplicate,
  onUnpublish,
  onPublish,
  onDelete,
  onAddOccurrence,
  onCancel,
  onRestore,
  onInstagram,
  isSeriesMenu = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Calculate if dropdown should open upward (near bottom of viewport)
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const estimatedMenuHeight = 250; // Approximate max height of dropdown
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      // Open upward if not enough space below AND more space above
      setOpenUpward(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow);
    }
  }, [isOpen]);

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowConfirm(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setShowConfirm(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
    setShowConfirm(false);
  }, []);

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onEdit();
  }, [onEdit]);

  const handleDuplicate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onDuplicate();
  }, [onDuplicate]);

  const handleUnpublishClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirm(true);
  }, []);

  const handleUnpublishConfirm = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    setShowConfirm(false);
    onUnpublish?.();
  }, [onUnpublish]);

  const handleUnpublishCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirm(false);
  }, []);

  const handlePublish = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onPublish?.();
  }, [onPublish]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onDelete?.();
  }, [onDelete]);

  const handleAddOccurrence = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onAddOccurrence?.();
  }, [onAddOccurrence]);

  const handleCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    onCancel?.();
  }, [onCancel]);

  const handleRestore = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onRestore?.();
  }, [onRestore]);

  const handleInstagram = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    onInstagram?.();
  }, [onInstagram]);

  // Determine which actions to show based on activeTab and eventStatus
  // - Past: Only "Duplicate"
  // - Published (active): "Edit", "Duplicate", "Cancel", "Add date" (series only)
  // - Cancelled: "Restore", "Duplicate"
  // - Draft: "Edit", "Duplicate", "Publish", "Delete", "Add date" (series only)
  const getShowCondition = (actionId: EventAction): boolean => {
    switch (actionId) {
      case 'edit':
        return activeTab !== 'past' && eventStatus !== 'cancelled';
      case 'duplicate':
        return true; // Always show duplicate
      case 'unpublish':
        // Hide unpublish - use cancel instead for active events
        return false;
      case 'publish':
        return activeTab === 'draft' && eventStatus === 'draft';
      case 'delete':
        return activeTab === 'draft' && eventStatus === 'draft';
      case 'addOccurrence':
        // Only show for series menus, not in past tab, not for cancelled
        return isSeriesMenu && activeTab !== 'past' && eventStatus !== 'cancelled' && !!onAddOccurrence;
      case 'cancel':
        // Show cancel for active events (not draft, not already cancelled)
        return activeTab === 'active' && eventStatus === 'active' && !!onCancel;
      case 'restore':
        // Show restore only for cancelled events
        return eventStatus === 'cancelled' && !!onRestore;
      case 'instagram':
        // Show Instagram post generation for active events
        return activeTab === 'active' && eventStatus === 'active' && !!onInstagram;
      default:
        return false;
    }
  };

  const menuItems: MenuItem[] = [
    {
      id: 'edit',
      label: isSeriesMenu ? 'Edit series' : 'Edit',
      icon: 'Edit',
      show: getShowCondition('edit')
    },
    {
      id: 'instagram',
      label: 'Instagram post',
      icon: 'Instagram',
      show: getShowCondition('instagram')
    },
    {
      id: 'addOccurrence',
      label: 'Add one-off date',
      icon: 'CalendarPlus',
      show: getShowCondition('addOccurrence')
    },
    {
      id: 'duplicate',
      label: activeTab === 'past' ? 'Renew' : (isSeriesMenu ? 'Duplicate series' : 'Duplicate'),
      icon: activeTab === 'past' ? 'RefreshCw' : 'Copy',
      show: getShowCondition('duplicate')
    },
    {
      id: 'publish',
      label: isSeriesMenu ? 'Publish series' : 'Publish',
      icon: 'Send',
      show: getShowCondition('publish')
    },
    {
      id: 'restore',
      label: isSeriesMenu ? 'Restore series' : 'Restore',
      icon: 'RotateCcw',
      show: getShowCondition('restore')
    },
    {
      id: 'cancel',
      label: isSeriesMenu ? 'Cancel series' : 'Cancel event',
      icon: 'XCircle',
      isDanger: true,
      show: getShowCondition('cancel')
    },
    {
      id: 'unpublish',
      label: isSeriesMenu ? 'Unpublish series' : 'Unpublish',
      icon: 'EyeOff',
      isDanger: true,
      show: getShowCondition('unpublish')
    },
    {
      id: 'delete',
      label: isSeriesMenu ? 'Delete series' : 'Delete',
      icon: 'Trash2',
      isDanger: true,
      show: getShowCondition('delete')
    }
  ];

  const visibleItems = menuItems.filter(item => item.show);

  return (
    <div className="event-card-menu" onClick={(e) => e.stopPropagation()}>
      {/* Trigger button (kebab menu) */}
      <button
        ref={buttonRef}
        className="event-card-menu__trigger"
        onClick={handleToggle}
        aria-label="Menu actions"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Icon name="MoreVertical" size={20} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className={`event-card-menu__dropdown ${openUpward ? 'event-card-menu__dropdown--upward' : ''}`}
          role="menu"
        >
          {!showConfirm ? (
            <>
              {visibleItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  {item.isDanger && index > 0 && (
                    <div className="event-card-menu__separator" />
                  )}
                  <button
                    className={`event-card-menu__item ${item.isDanger ? 'event-card-menu__item--danger' : ''}`}
                    onClick={
                      item.id === 'edit' ? handleEdit :
                      item.id === 'duplicate' ? handleDuplicate :
                      item.id === 'publish' ? handlePublish :
                      item.id === 'delete' ? handleDelete :
                      item.id === 'addOccurrence' ? handleAddOccurrence :
                      item.id === 'cancel' ? handleCancel :
                      item.id === 'restore' ? handleRestore :
                      item.id === 'instagram' ? handleInstagram :
                      handleUnpublishClick
                    }
                    role="menuitem"
                  >
                    <Icon name={item.icon as any} size={18} />
                    <span>{item.label}</span>
                  </button>
                </React.Fragment>
              ))}
            </>
          ) : (
            <div className="event-card-menu__confirm">
              <p className="event-card-menu__confirm-text">
                Unpublish this event?
              </p>
              <div className="event-card-menu__confirm-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleUnpublishCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleUnpublishConfirm}
                >
                  Confirm
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventCardMenu;
